/**
 * Was auf dem Lageplan gezeichnet ist: Gruben, Gräben und ihre Länge.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „Lageplan: oben Anzahl geplanter Gruben, geplanter Gräben, Länge der
 *    Gräben"
 *
 * DIE ZAHLEN STANDEN UNTER DER KARTE UND OHNE METER. Wer zeichnet, sieht auf
 * die Karte; was unter ihr steht, liest er, wenn er fertig ist - und die
 * Länge stand dort gar nicht. Sie ist aber die Zahl, mit der man den Antrag
 * an die Gemeinde gegenprüft: eine Aufgrabungsanzeige nennt die Länge, und
 * wer sie erst nach dem Erzeugen sieht, sieht sie zu spät.
 *
 * WARUM DIE RECHNUNG HIER STEHT UND NICHT IN DER KOMPONENTE. Sie stand als
 * `meterZwischen` in `seiten/Tiefbauplanung.tsx`, also in einer Datei mit
 * JSX - und die lässt sich mit `--experimental-strip-types` nicht laden. **Eine
 * Textsuche misst, dass etwas dasteht, nicht, dass es stimmt**; ob zwei
 * Fassungen derselben Formel dasselbe ergeben, muss man ausrechnen.
 * Dreizehnte Datei dieser Art nach `namen.ts`, `terminraster.ts`,
 * `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`, `kartenebene.ts`,
 * `grenzpunkte.ts`, `handbuchmarken.ts`, `tabellenbeschriftung.ts`,
 * `zahlwoerter.ts`, `bruchstellen.ts` und `anlasstext.ts`.
 *
 * UND SIE IST EINE ANZEIGE UND KEINE BUCHFÜHRUNG. Was gespeichert wird, sind
 * die PUNKTE; die Meterzahl der Tiefbauschätzung rechnet weiterhin der
 * Server aus dem Verlauf (`regeln/tiefbau.ts`). Zwei Rechenwege für dieselbe
 * GESPEICHERTE Zahl wären die Stelle, an der ein System auseinanderläuft -
 * hier wird nichts gespeichert, hier wird gezeigt, was man gerade gezeichnet
 * hat. Prüfstand 123 legt beide Fassungen über eine Reihe von Punkten
 * nebeneinander.
 */

export interface Punkt { lat: number; lng: number }

/** Eine Markierung des Lageplans: eine Grube ist ein Punkt, ein Graben ein Zug. */
export interface Markierung { art: 'grube' | 'graben'; punkte: Punkt[] }

const ERDRADIUS_M = 6_371_000;

/**
 * Luftlinie zwischen zwei Punkten, in Metern (Haversine).
 *
 * WÖRTLICH DIESELBE FORMEL WIE IN `api/src/regeln/tiefbau.ts`, mit demselben
 * Erdradius. Sie steht hier ein zweites Mal, weil der Browser den
 * Serverquelltext nicht laden kann - und genau deshalb wird sie gemessen und
 * nicht angenommen.
 */
export function meterZwischen(a: Punkt, b: Punkt): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * ERDRADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Die Länge EINES Zuges: die Summe seiner Abschnitte.
 *
 * EIN ZUG MIT EINEM EINZIGEN PUNKT IST NULL METER LANG und keine Lücke. So
 * entsteht jeder Graben: der erste Klick setzt den Anfang, der zweite macht
 * daraus eine Strecke. Wer den ersten Klick als Fehler behandelt, meldet
 * mitten im Zeichnen einen Mangel.
 */
export function zuglaenge(punkte: Punkt[]): number {
  let m = 0;
  for (let i = 1; i < punkte.length; i++) m += meterZwischen(punkte[i - 1]!, punkte[i]!);
  return m;
}

export interface Lageplanzahlen {
  gruben: number;
  graeben: number;
  /** Die Summe aller Grabenzüge, in Metern. */
  meter: number;
}

/**
 * Was oben über der Karte steht.
 *
 * GEZÄHLT WERDEN MARKIERUNGEN UND NICHT KLICKS. Ein Graben ist EIN Graben,
 * auch wenn er über sechs Klicks entstanden ist - sonst meldet der Bildschirm
 * sechs Gräben, wo einer liegt, und die Zahl im Antrag an die Gemeinde wäre
 * eine andere als die auf dem Blatt.
 *
 * UND EINE GRUBE TRÄGT KEINE METER. Sie ist ein Loch und keine Strecke; wer
 * ihre Punkte mitsummierte, addierte den Abstand zwischen zwei Gruben zur
 * Grabenlänge - eine Zahl, die zu gross ist, sieht aus wie eine Zahl.
 */
export function lageplanZahlen(mark: Markierung[]): Lageplanzahlen {
  let gruben = 0, graeben = 0, meter = 0;
  for (const m of mark ?? []) {
    if (m?.art === 'grube') { gruben++; continue; }
    if (m?.art !== 'graben') continue;
    graeben++;
    meter += zuglaenge(m.punkte ?? []);
  }
  return { gruben, graeben, meter };
}

/**
 * Meter für Menschen: Dezimalkomma, eine Nachkommastelle bis 100 m, darüber
 * ganze Meter.
 *
 * EINE MARKIERUNG IST SYMBOLISCH UND NICHT VERMESSEN - das steht seit dem
 * 20.08.2026 unter jeder dieser Karten. „248,73 m" behauptete eine
 * Genauigkeit, die ein Mausklick nicht hergibt; über hundert Metern ist die
 * Nachkommastelle Zierde.
 */
export function meterText(m: number): string {
  if (!Number.isFinite(m) || m <= 0) return '0 m';
  return m < 100
    ? m.toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' m'
    : Math.round(m).toLocaleString('de-DE') + ' m';
}
